import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { BASE_URL } from '../Config';
import keepaiLogo from '../resource/keepaiLogo.png';
import '../css/style/groupchat.css';

const getAuth = () => {
    const s = localStorage.getItem('user');
    return s ? JSON.parse(s) : null;
};

const getMyId = () => {
    try {
        const user = getAuth();
        if (!user?.token) return null;
        return JSON.parse(atob(user.token.split('.')[1])).userId;
    } catch { return null; }
};

const PRODUCT_LABELS = {
    chatgpt: 'ChatGPT Plus',
    claude:  'Claude Pro',
    codex:   'GPT-5.3 Codex',
    netflix: 'Netflix',
    tving:   'Tiving',
};

const fmtTime = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
};

const GroupChat = () => {
    const navigate = useNavigate();
    const user = getAuth();
    const myId = getMyId();
    const [rooms, setRooms] = useState([]);
    const [activeRoom, setActiveRoom] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [showSidebar, setShowSidebar] = useState(true);
    const [connected, setConnected] = useState(false);
    const socketRef = useRef(null);
    const activeRoomRef = useRef(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        if (!user?.token) { navigate('/login'); return; }

        fetch(`${BASE_URL}/my-rooms`, {
            headers: { Authorization: `Bearer ${user.token}` },
        })
            .then(r => r.json())
            .then(data => setRooms(Array.isArray(data) ? data : []))
            .catch(console.error);

        const socketUrl = BASE_URL.replace(/\/api$/, '') || window.location.origin;
        const socket = io(socketUrl, {
            auth: { token: user.token },
            transports: ['websocket', 'polling'],
        });
        socketRef.current = socket;

        socket.on('connect', () => setConnected(true));
        socket.on('disconnect', () => setConnected(false));
        socket.on('message_history', (msgs) => setMessages(msgs || []));
        socket.on('new_message', (msg) => {
            if (msg.ROOM_ID === activeRoomRef.current?.ROOM_ID) {
                setMessages(prev => [...prev, msg]);
            }
        });
        socket.on('room_error', (msg) => console.error('[SOCKET]', msg));
        socket.on('connect_error', (err) => console.error('[SOCKET] 연결 오류:', err.message));

        return () => socket.disconnect();
    }, []); // eslint-disable-line

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSelectRoom = useCallback((room) => {
        if (activeRoomRef.current) {
            socketRef.current?.emit('leave_room', { roomId: activeRoomRef.current.ROOM_ID });
        }
        activeRoomRef.current = room;
        setActiveRoom(room);
        setMessages([]);
        socketRef.current?.emit('join_room', { roomId: room.ROOM_ID });
        if (window.innerWidth < 768) setShowSidebar(false);
    }, []);

    const handleSend = () => {
        const text = input.trim();
        if (!text || !activeRoom) return;
        socketRef.current?.emit('send_message', { roomId: activeRoom.ROOM_ID, content: text });
        setInput('');
    };

    return (
        <div className="gc-wrap">
            <header className="gc-hdr">
                <img src={keepaiLogo} alt="KeepAI" className="gc-logo" onClick={() => navigate('/')} />
                <button className="gc-sidebar-toggle" onClick={() => setShowSidebar(v => !v)}>☰</button>
                <span className="gc-hdr-title">{activeRoom ? activeRoom.NAME : '단톡방'}</span>
                <span className={`gc-conn-dot ${connected ? 'gc-conn-dot--on' : ''}`} title={connected ? '연결됨' : '연결 중...'} />
                <button className="gc-logout" onClick={() => { localStorage.removeItem('user'); navigate('/login'); }}>로그아웃</button>
            </header>

            <div className="gc-body">
                {/* 사이드바 오버레이 (모바일) */}
                {showSidebar && window.innerWidth < 768 && (
                    <div className="gc-overlay" onClick={() => setShowSidebar(false)} />
                )}

                <aside className={`gc-sidebar ${showSidebar ? 'gc-sidebar--open' : ''}`}>
                    <div className="gc-sidebar-hdr">내 채팅방</div>
                    {rooms.length === 0
                        ? <div className="gc-no-rooms">참여 중인 방이 없습니다.<br />관리자에게 문의하세요.</div>
                        : rooms.map(room => (
                            <div
                                key={room.ROOM_ID}
                                className={`gc-room-item ${activeRoom?.ROOM_ID === room.ROOM_ID ? 'active' : ''}`}
                                onClick={() => handleSelectRoom(room)}
                            >
                                <div className="gc-room-name">{room.NAME}</div>
                                <div className="gc-room-meta">
                                    <span className={`gc-product-badge gc-product-${room.PRODUCT_TYPE}`}>
                                        {PRODUCT_LABELS[room.PRODUCT_TYPE] || room.PRODUCT_TYPE}
                                    </span>
                                    <span className="gc-room-count">{room.MEMBER_COUNT}/{room.MAX_MEMBERS}명</span>
                                </div>
                            </div>
                        ))
                    }
                </aside>

                <main className="gc-main">
                    {!activeRoom ? (
                        <div className="gc-empty">
                            <div className="gc-empty-icon">💬</div>
                            <p>채팅방을 선택해주세요</p>
                        </div>
                    ) : (
                        <>
                            <div className="gc-chat-hdr">
                                <strong>{activeRoom.NAME}</strong>
                                <span className={`gc-product-badge gc-product-${activeRoom.PRODUCT_TYPE}`}>
                                    {PRODUCT_LABELS[activeRoom.PRODUCT_TYPE] || activeRoom.PRODUCT_TYPE}
                                </span>
                                <span className="gc-chat-count">{activeRoom.MEMBER_COUNT}/{activeRoom.MAX_MEMBERS}명</span>
                            </div>

                            <div className="gc-messages">
                                {messages.map((msg, i) => {
                                    const isMine = msg.USERNAME === myId;
                                    return (
                                        <div key={i} className={`gc-msg-wrap ${isMine ? 'gc-msg-wrap--mine' : ''}`}>
                                            {!isMine && <div className="gc-msg-name">{msg.USERNAME}</div>}
                                            <div className="gc-msg-row">
                                                {isMine && <span className="gc-msg-time">{fmtTime(msg.CREATED_AT)}</span>}
                                                <div className={`gc-bubble ${isMine ? 'gc-bubble--mine' : ''}`}>{msg.CONTENT}</div>
                                                {!isMine && <span className="gc-msg-time">{fmtTime(msg.CREATED_AT)}</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={bottomRef} />
                            </div>

                            <div className="gc-input-area">
                                <input
                                    className="gc-input"
                                    placeholder="메시지를 입력하세요..."
                                    value={input}
                                    onChange={e => setInput(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                                />
                                <button className="gc-send-btn" onClick={handleSend}>전송</button>
                            </div>
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

export default GroupChat;